import {
  ContractAnalysisRow,
  FloppyDiskIcon,
  ModalDialog,
  Mtds,
  useUpdateAnalysisTypeMutation
} from '../..'
import { fields, templates, widgets } from '../ui-schema-components'
import Form, { type IChangeEvent } from '@rjsf/core'
import { customizeValidator } from '@rjsf/validator-ajv8'

import { type RJSFSchema } from '@rjsf/utils'
import { useQueryClient } from '@tanstack/react-query'

export interface ReviewCombinationFormData {
  decision: boolean
  reason: string
}

const schema: RJSFSchema = {
  type: 'object',
  properties: {
    decision: {
      type: 'boolean',
      title: 'Decision',
      default: true,
      oneOf: [
        { const: true, title: 'Approve' },
        { const: false, title: 'Decline' }
      ]
    }
  },
  required: ['decision'],
  dependencies: {
    decision: {
      oneOf: [
        {
          properties: {
            decision: { const: true }
          }
        },
        {
          properties: {
            decision: { const: false },
            reason: {
              type: 'string',
              title: 'Reason',
              maxLength: 1000
            }
          },
          required: ['reason']
        }
      ]
    }
  }
}

export const uiSchema = {
  'ui:order': ['decision', 'reason'],
  decision: {
    'ui:widget': 'radio'
  },
  reason: {
    'ui:widget': 'textarea',
    'ui:placeholder': 'Reason for declining...',
    'ui:options': {
      rows: 5
    }
  }
}

type ReviewCombinationModalProps = {
  review: ContractAnalysisRow[] | undefined
  onClose: () => void
  id?: string
}

export const ReviewCombinationModal = ({
  review,
  onClose,
  id = 'review-combination-modal'
}: ReviewCombinationModalProps) => {
  const queryClient = useQueryClient()
  const isOpen = Array.isArray(review) && review.length > 0
  const update = useUpdateAnalysisTypeMutation()
  const reviewCount = review?.length ?? 0
  const headingText =
    reviewCount === 1
      ? 'Review combination'
      : `Review ${reviewCount} combinations`
  const descriptionText =
    reviewCount === 1
      ? 'Choose approve or decline for the selected combination. Provide a reason if you decline.'
      : 'You are reviewing multiple combinations. The chosen decision applies to all selected combinations. Provide a reason if you decline.'

  const processApprove = async (rows: ContractAnalysisRow[]) => {
    const results: string[] = []
    for (const row of rows) {
      try {
        await update.mutateAsync({
          contractId: row.contractId,
          analysisTypeId: row.analysisType.id,
          body: {
            // The legacy API clears the stored price if it is omitted.
            price: row.analysisType.price,
            mtApproval: row.analysisType.mtApproval,
            labApproval: true,
            approvalComment: row.analysisType.approvalComment
          }
        })
        results.push(`Approved #${row.analysisType.id}`)
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Unknown error'
        results.push(`Failed approve #${row.analysisType.id}: ${message}`)
      }
    }
    return results
  }

  const processReject = async (rows: ContractAnalysisRow[], reason: string) => {
    const results: string[] = []
    for (const row of rows) {
      try {
        await update.mutateAsync({
          contractId: row.contractId,
          analysisTypeId: row.analysisType.id,
          body: {
            price: row.analysisType.price,
            mtApproval: row.analysisType.mtApproval,
            labApproval: false,
            approvalComment: reason
          }
        })
        results.push(`Declined #${row.analysisType.id}`)
      } catch (e: unknown) {
        const message = e instanceof Error ? e.message : 'Unknown error'
        results.push(`Failed decline #${row.analysisType.id}: ${message}`)
      }
    }
    return results
  }

  const onSubmit = async ({
    formData
  }: IChangeEvent<ReviewCombinationFormData>): Promise<void> => {
    if (!formData || !review?.length) return
    const rows = review
    let messages: string[]
    if (formData.decision) {
      messages = await processApprove(rows)
    } else {
      messages = await processReject(rows, formData.reason)
    }
    const anyFailed = messages.some((m) => m.startsWith('Failed'))
    if (anyFailed) {
      Mtds.toast.danger(formData.decision ? 'Approve failed' : 'Decline failed')
    } else {
      const count = rows.length
      const action = formData.decision ? 'approved' : 'declined'
      const prefix = count === 1 ? 'Combination' : `${count} combinations`
      Mtds.toast.success(`${prefix} ${action} successfully`)
    }
    await queryClient.invalidateQueries({
      queryKey: ['contracts']
    })
    onClose()
  }
  const validator = customizeValidator<ReviewCombinationFormData>()
  return (
    <ModalDialog
      id={id}
      closedby='any'
      open={isOpen}
      onClose={onClose}
      data-color='main'
    >
      <Mtds.Button command='close' commandfor={id} aria-label='Close' />
      <Mtds.Prose>
        <Mtds.Heading>{headingText}</Mtds.Heading>
        <p>{descriptionText}</p>
        {isOpen && (
          <Form<ReviewCombinationFormData>
            noHtml5Validate
            schema={schema}
            uiSchema={uiSchema}
            widgets={widgets}
            fields={fields}
            templates={templates}
            showErrorList={false}
            validator={validator}
            experimental_defaultFormStateBehavior={{
              constAsDefaults: 'skipOneOf'
            }}
            onSubmit={onSubmit}
          >
            <Mtds.Flex className='mt-8'>
              <Mtds.Button
                aria-busy={update.isPending}
                data-variant='primary'
                disabled={update.isPending}
                type='submit'
              >
                <FloppyDiskIcon />
                Save
              </Mtds.Button>

              <Mtds.Button command='close' commandfor={id}>
                Cancel
              </Mtds.Button>
            </Mtds.Flex>
          </Form>
        )}
      </Mtds.Prose>
    </ModalDialog>
  )
}
